import {
   ICadenceStep,
   ICampaignConfiguration,
   IContentPool,
   IContentReference,
   ICriteria,
   IPreview,
   TCadenceAction,
   TCriteriaOperator,
} from '../interfaces/campaign-v2.types'
import { defaultPoolId } from './names'

/* ---------- New configuration ---------- */

export const SELECTION_POLICY = {
   phone: 'round_robin',
   strategy: 'ordered_journey_v1',
   exhaustion: 'repeat-last',
} as const

export const emptyPool = (
   businessTypeId: number,
   categoryLabel?: string
): IContentPool => ({
   id: defaultPoolId(categoryLabel, businessTypeId),
   businessTypeId,
   isDefault: true,
   outboundScripts: [],
   inboundScripts: [],
   strategies: [],
   conversationContent: [],
   personas: [],
})

/** The cadence a fresh campaign starts with: the general reactivate shape —
 * calls across four months with two light texts riding the early calls. */
export const DEFAULT_CADENCE: ICadenceStep[] = [
   ...[1, 5, 12, 25, 50, 85, 120].map((day, i) => ({
      id: `call-${i + 1}`,
      order: 0,
      day,
      localTime: '11:00',
      action: 'call' as const,
      voicemail: 'if-configured' as const,
   })),
   { id: 'sms-1', order: 0, day: 2, localTime: '16:30', action: 'sms' as const },
   { id: 'sms-2', order: 0, day: 6, localTime: '16:30', action: 'sms' as const },
]
   .sort((a, b) => a.day - b.day)
   .map((c, i) => ({ ...c, order: i + 1 }))

export const newConfiguration = (
   businessTypeIds: number[],
   labels: Record<number, string> = {}
): ICampaignConfiguration => ({
   schemaVersion: 2,
   businessTypeIds,
   source: { mode: 'manual' },
   routing: { enabled: false, strategy: 'round_robin', agentIds: [] },
   phoneNumbers: [],
   contentPools: businessTypeIds.map((id) => emptyPool(id, labels[id])),
   subtypes: [],
   cadence: DEFAULT_CADENCE.map((c) => ({ ...c })),
   selectionPolicy: { ...SELECTION_POLICY },
})

/**
 * Keep pools in step with the chosen categories: one default pool per
 * category, existing pools kept, removed categories dropped.
 */
export const syncPools = (
   config: ICampaignConfiguration,
   businessTypeIds: number[],
   labels: Record<number, string> = {}
): ICampaignConfiguration => {
   const kept = config.contentPools.filter((p) =>
      businessTypeIds.includes(p.businessTypeId)
   )
   const missing = businessTypeIds.filter(
      (id) => !kept.some((p) => p.businessTypeId === id)
   )
   const pools = [...kept, ...missing.map((id) => emptyPool(id, labels[id]))]
   const subtypes = config.subtypes.filter((s) =>
      pools.some((p) => p.id === s.poolId)
   )
   const rules =
      businessTypeIds.length > 1
         ? (config.businessTypeRules || []).filter((r) =>
              businessTypeIds.includes(r.businessTypeId)
           )
         : undefined
   const incomingCallPoolId =
      config.incomingCallPoolId &&
      pools.some((p) => p.id === config.incomingCallPoolId)
         ? config.incomingCallPoolId
         : undefined
   return {
      ...config,
      businessTypeIds,
      businessTypeRules: rules,
      contentPools: pools,
      subtypes,
      incomingCallPoolId,
   }
}

/* ---------- References ---------- */

export const sameRef = (a: IContentReference, b: IContentReference) =>
   a.id === b.id

export const addRef = (list: IContentReference[], ref: IContentReference) =>
   list.some((r) => sameRef(r, ref))
      ? list.map((r) => (sameRef(r, ref) ? ref : r))
      : [...list, ref]

export const removeRef = (list: IContentReference[], id: string) =>
   list.filter((r) => r.id !== id)

/** Swap one identity for another (customize returns a new contentId). */
export const replaceRef = (
   list: IContentReference[],
   oldId: string,
   next: IContentReference
) =>
   list.some((r) => r.id === oldId)
      ? list.map((r) => (r.id === oldId ? next : r))
      : [...list, next]

/* ---------- Cadence ---------- */

export const renumber = (cadence: ICadenceStep[]): ICadenceStep[] =>
   [...cadence]
      .sort(
         (a, b) =>
            a.day - b.day ||
            a.localTime.localeCompare(b.localTime) ||
            a.order - b.order
      )
      .map((c, i) => ({ ...c, order: i + 1 }))

export const nextCadenceId = (
   cadence: ICadenceStep[],
   action: TCadenceAction
) => {
   let n = cadence.filter((c) => c.action === action).length + 1
   while (cadence.some((c) => c.id === `${action}-${n}`)) n += 1
   return `${action}-${n}`
}

export const addCadenceStep = (
   cadence: ICadenceStep[],
   action: TCadenceAction
): ICadenceStep[] => {
   const last = cadence[cadence.length - 1]
   const step: ICadenceStep = {
      id: nextCadenceId(cadence, action),
      order: cadence.length + 1,
      day: last ? last.day + 1 : 1,
      localTime: last?.localTime || '10:00',
      action,
      ...(action === 'call' ? { voicemail: 'if-configured' as const } : {}),
   }
   return renumber([...cadence, step])
}

export const updateCadenceStep = (
   cadence: ICadenceStep[],
   id: string,
   patch: Partial<ICadenceStep>
): ICadenceStep[] =>
   renumber(
      cadence.map((c) => {
         if (c.id !== id) return c
         const next = { ...c, ...patch }
         if (next.action !== 'call') delete next.voicemail
         else if (!next.voicemail) next.voicemail = 'if-configured'
         return next
      })
   )

export const removeCadenceStep = (cadence: ICadenceStep[], id: string) =>
   renumber(cadence.filter((c) => c.id !== id))

/** Which channels the cadence uses, so the content step knows what is required. */
export const channelsUsed = (cadence: ICadenceStep[]) => ({
   call: cadence.some((c) => c.action === 'call'),
   sms: cadence.some((c) => c.action === 'sms'),
   email: cadence.some((c) => c.action === 'email'),
   voicemail: cadence.some(
      (c) => c.action === 'call' && c.voicemail === 'if-configured'
   ),
})

export const cadenceSpanDays = (cadence: ICadenceStep[]) =>
   cadence.reduce((m, c) => Math.max(m, c.day), 0)

/** "9 am" from "09:00". */
export const clock = (t: string) => {
   const [h, m] = (t || '').split(':').map(Number)
   if (Number.isNaN(h)) return t
   const suffix = h >= 12 ? 'pm' : 'am'
   const hour = h % 12 || 12
   return m
      ? `${hour}:${String(m).padStart(2, '0')} ${suffix}`
      : `${hour} ${suffix}`
}

export const ACTION_LABEL: Record<TCadenceAction, string> = {
   call: 'Call',
   sms: 'Text',
   email: 'Email',
}

/** "5 touches over 4 days · 2 calls, 2 texts, 1 email" */
export const cadenceSentence = (cadence: ICadenceStep[]) => {
   if (!cadence.length) return 'No touches yet'
   const counts = cadence.reduce(
      (acc, c) => ({ ...acc, [c.action]: (acc[c.action] || 0) + 1 }),
      {} as Record<TCadenceAction, number>
   )
   const parts = (['call', 'sms', 'email'] as TCadenceAction[])
      .filter((a) => counts[a])
      .map(
         (a) =>
            `${counts[a]} ${ACTION_LABEL[a].toLowerCase()}${counts[a] === 1 ? '' : 's'}`
      )
   const days = cadenceSpanDays(cadence)
   return `${cadence.length} touch${cadence.length === 1 ? '' : 'es'} over ${days} day${days === 1 ? '' : 's'} · ${parts.join(', ')}`
}

/* ---------- Audience criteria ---------- */

export interface ICriteriaField {
   id: string
   label: string
   /** How the chip reads: "from Zillow" / "in Lead" / "tagged hot". */
   phrase: (values: string[], negated: boolean) => string
   operators: Array<{ id: TCriteriaOperator; label: string }>
   /** Which picker to show for the value. */
   picker: 'source' | 'stage' | 'tags' | 'pond' | 'number' | 'date' | 'text'
}

const list = (values: string[]) =>
   values.length <= 1
      ? values.join('')
      : `${values.slice(0, -1).join(', ')} or ${values[values.length - 1]}`

export const CRITERIA_FIELDS: ICriteriaField[] = [
   {
      id: 'source',
      label: 'Lead source',
      phrase: (v, n) => `${n ? 'not ' : ''}from ${list(v)}`,
      operators: [
         { id: 'IN', label: 'is' },
         { id: 'NOT IN', label: 'is not' },
      ],
      picker: 'source',
   },
   {
      id: 'stage',
      label: 'Stage',
      phrase: (v, n) => `${n ? 'not ' : ''}in ${list(v)}`,
      operators: [
         { id: 'IN', label: 'is' },
         { id: 'NOT IN', label: 'is not' },
      ],
      picker: 'stage',
   },
   {
      id: 'tags',
      label: 'Tag',
      phrase: (v, n) => `${n ? 'not ' : ''}tagged ${list(v)}`,
      operators: [
         { id: 'CONTAINS_ANY', label: 'has any of' },
         { id: 'CONTAINS_ALL', label: 'has all of' },
         { id: 'NOT_CONTAINS_ANY', label: 'has none of' },
      ],
      picker: 'tags',
   },
   {
      id: 'assigned_pond_id',
      label: 'Pond',
      phrase: (v) => `in pond ${list(v)}`,
      operators: [{ id: 'IN', label: 'is' }],
      picker: 'pond',
   },
   {
      id: 'lead_price',
      label: 'Price',
      phrase: (v) => `priced ${list(v)}`,
      operators: [
         { id: '>=', label: 'at least' },
         { id: '<=', label: 'at most' },
      ],
      picker: 'number',
   },
   {
      id: 'created_at',
      label: 'Created',
      phrase: (v, n) => `created ${n ? 'before' : 'after'} ${list(v)}`,
      operators: [
         { id: '>=', label: 'after' },
         { id: '<=', label: 'before' },
      ],
      picker: 'date',
   },
   {
      id: 'last_activity_date',
      label: 'Last activity',
      phrase: (v, n) => `last active ${n ? 'before' : 'after'} ${list(v)}`,
      operators: [
         { id: '>=', label: 'after' },
         { id: '<=', label: 'before' },
      ],
      picker: 'date',
   },
]

export const criteriaField = (id: string) =>
   CRITERIA_FIELDS.find((f) => f.id === id)

const NEGATED: TCriteriaOperator[] = [
   'NOT IN',
   'NOT_CONTAINS_ANY',
   '!=',
   '<>',
   '<=',
   '<',
]

export const criteriaValues = (c: ICriteria): string[] =>
   Array.isArray(c.value)
      ? c.value.map(String)
      : c.value === undefined
        ? []
        : [String(c.value)]

/** One readable phrase per criterion: "from Zillow", "tagged hot". */
export const criteriaPhrase = (
   c: ICriteria,
   labels: Record<string, string> = {}
) => {
   const field = criteriaField(c.field)
   const values = criteriaValues(c).map((v) => labels[`${c.field}:${v}`] || v)
   if (!field) return `${c.field} ${c.operator} ${values.join(', ')}`
   return field.phrase(values, NEGATED.includes(c.operator))
}

/** "Leads from Zillow or Realtor, in Lead, tagged hot" */
export const audienceSentence = (
   criteria: ICriteria[],
   labels?: Record<string, string>
) =>
   criteria.length
      ? `Leads ${criteria.map((c) => criteriaPhrase(c, labels)).join(', ')}`
      : 'Every lead that qualifies'

/** Backend contract: flat list, `logic` on every criterion after the first. */
export const normalizeCriteria = (criteria: ICriteria[]): ICriteria[] =>
   criteria.map((c, i) => {
      const out: ICriteria = { field: c.field, operator: c.operator }
      if (c.operator !== 'IS NULL' && c.operator !== 'IS NOT NULL')
         out.value = c.value
      if (i > 0) out.logic = c.logic || 'AND'
      return out
   })

/* ---------- Idempotency ---------- */

export const idempotencyKey = (scope: string) => {
   const rand =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
         ? crypto.randomUUID()
         : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
   return `${scope}-${rand}`
}

/* ---------- Readiness ---------- */

/** What the backend's readiness reasons mean in plain words, with where to fix them. */
export const READINESS_HINTS: Array<{
   match: RegExp
   text: string
   step: string
}> = [
   {
      match: /phone.?number/i,
      text: 'Pick at least one campaign number.',
      step: 'numbers',
   },
   {
      match: /outbound|outgoing/i,
      text: 'Add an outgoing call script to every category.',
      step: 'content',
   },
   {
      match: /inbound|incoming/i,
      text: 'Add an incoming call script to every category.',
      step: 'content',
   },
   {
      match: /conversation|reply/i,
      text: 'Add reply instructions to every category.',
      step: 'content',
   },
   {
      match: /strateg|sms|email|message/i,
      text: 'Add a outbound content group with the channels your cadence uses.',
      step: 'content',
   },
   {
      match: /routing|agent/i,
      text: 'Choose who receives the leads, or turn routing off.',
      step: 'team',
   },
   {
      match: /assistant|voice/i,
      text: 'Add a voice for the campaign.',
      step: 'voice',
   },
   {
      match: /cadence/i,
      text: 'Add at least one touch to the cadence.',
      step: 'cadence',
   },
   {
      match: /email.*(provider|sender)/i,
      text: 'Email sending is not set up for your organization yet.',
      step: 'cadence',
   },
]

export const explainReason = (reason: string) =>
   READINESS_HINTS.find((h) => h.match.test(reason)) || {
      text: reason,
      step: 'review',
   }

/** The client-side checks that match section 6 of the handoff. */
export const localReadiness = (
   config: ICampaignConfiguration,
   assistants: number
): string[] => {
   const reasons: string[] = []
   const used = channelsUsed(config.cadence)
   if (!config.cadence.length) reasons.push('cadence_empty')
   config.contentPools.forEach((p) => {
      if (used.call && !p.outboundScripts.length)
         reasons.push(`outbound_script_missing:${p.id}`)
      if (!p.inboundScripts.length)
         reasons.push(`inbound_script_missing:${p.id}`)
      if (!p.conversationContent.length)
         reasons.push(`conversation_missing:${p.id}`)
      if ((used.sms || used.email) && !p.strategies.length)
         reasons.push(`strategy_missing:${p.id}`)
   })
   if (config.routing.enabled && !config.routing.agentIds.length)
      reasons.push('routing_agents_missing')
   if (!(config.phoneNumbers || []).length)
      reasons.push('campaign_phone_number_required')
   if (!assistants) reasons.push('assistant_missing')
   return reasons
}

export const previewFromLocal = (
   config: ICampaignConfiguration,
   assistants: number
): IPreview => {
   const reasons = localReadiness(config, assistants)
   return {
      ready: reasons.length === 0,
      reasons,
      cadence: config.cadence,
      selectedPhoneNumbers: config.phoneNumbers || [],
      existingPhoneNumberCount: (config.phoneNumbers || []).length,
      numbersToPurchase: 0,
   }
}
